import React from 'react';
import './Experience.css';

const Experience = () => {
  const experience = [
    {
      role: 'Partner Solutions Architect',
      company: 'Weaviate',
      period: 'July 2024 - Nov 2025',
      description: 'Technical lead for strategic partner collaborations (AWS, GCP, PA Consulting), driving AI platform integrations and market enablement.',
      technologies: ['LangGraph', 'LlamaIndex', 'AWS SageMaker', 'Bedrock', 'CloudFormation', 'ArizeAI', 'Weights & Biases', 'CrewAI', 'Databricks', 'Vector Databases', 'RAG', 'Python', 'Node.js', 'Javascript/Typescript', 'Jupyter Notebooks', 'Embeddings', 'Flutter', 'Django'],
      highlights: [
        'Built a Weaviate MCP server to integrate with tools functionality of CrewAI framework',
        <>
          Developed production-ready agent applications with AI frameworks like
          LangGraph, LlamaIndex, Datastax, Amazon Sagemaker Unified Studio.
          Open source contributions including{' '}
          <a
            href="https://weaviate.io/blog/enterprise-workflow-langchain-weaviate"
            target="_blank"
            rel="noreferrer"
          >
            LangChain upgrade for Weaviate
          </a>
        </>,
        'Systematically optimized prompts for RAG systems using GPT-4 and Cohere models - measuring accuracy, relevance, and latency. Implemented few-shot examples, structured system prompts, and iterative refinement based on evaluation metrics',
        <>
          Authored technical blog series on evaluation and guardrails (
          <a
            href="https://weaviate.io/blog/evals-guardrails-enterprise-workflows-2"
            target="_blank"
            rel="noreferrer"
          >
            Part 2
          </a>
          ,{' '}
          <a
            href="https://weaviate.io/blog/evals-enterprise-workflows-3"
            target="_blank"
            rel="noreferrer"
          >
            Part 3
          </a>
          ) with Weights & Biases and Arize AI, demonstrating systematic
          approaches to prompt engineering, quality measurement, answer
          relevance, hallucination detection and production deployment patterns
        </>,
        'Developed AWS CloudFormation templates for marketplace listings, enabling seamless partner deployments',
        'Delivered enablement sessions and led AI architecture workshops for global partners',
        'Represented Weaviate at summits and authored multiple blog posts on efficient AI architectures, RAG and vector search.',
        'Developed hands-on tutorials and code examples',
      ]
    },
    {
      role: 'Solution Architect',
      company: 'General Systems',
      period: 'Jan 2023 - May 2024',
      description: 'Primary technical contact for business development, creating demonstrations and engineering support throughout the sales lifecycle.',
      highlights: [
        'Created technical presentations, demos, and PoCs for conferences and RFPs, translating complex geospatial capabilities for non-technical stakeholders.',
        'Conducted big data benchmarking across BigQuery, PostGIS, and Sedona for performance optimization',
        'Provided pre-sales engineering support and post-sales technical guidance to commercial teams',
      ],
      technologies: ['BigQuery', 'PostGIS', 'Apache Sedona', 'Geospatial Analytics', 'Big Data', 'Python', 'MySQL', 'Jupyter Notebooks']
    },
    {
      role: 'Senior Solution Architect (Pre and Post Sales)',
      company: 'Twilio',
      period: 'Jan 2020 - Nov 2022',
      description: 'Post-sales technical advisor and partner manager for enterprise implementations of Twilio\'s communication platform.',
      highlights: [
        'Inspected, advised, and trained customer teams on Twilio software architecture and best practices',
        'Analyzed complex customer systems and presented detailed architecture specifications',
        'Managed partner teams to deliver custom implementations on time and within scope',
        'Designed cloud infrastructure solutions across AWS and Azure for communications workloads'
      ],
      technologies: ['AWS', 'Azure', 'React', 'SIP/VOIP', 'WebRTC', 'Twilio APIs']
    },
    {
      role: 'Technical Architect (Platform)',
      company: 'Babylon Health',
      period: 'Jul 2019 - Dec 2019',
      description: 'Infrastructure lead with focus on cost optimization and HADR compliance.',
      highlights: [
        'Developed infrastructure design for enterprise automation platform',
        'Led company-wide AWS cost reduction, achieving significant savings through right-sizing and eliminating over-provisioning',
        'Implemented Kubernetes and RDS optimization across platform to improve resource utilization'
      ],
      technologies: ['AWS', 'Kubernetes', 'RDS', 'HADR-compliant infrastructure design']
    },
    {
      role: 'Principal Consultant',
      company: 'PA Consulting',
      period: 'September 2018 - June 2019',
      description: 'Technical lead for enterprise transformations and cloud migrations.',
      highlights: [
        'Led technical consolidation implementation (post-acquisition) for major UK photo publishing house, on AWS',
        'Provided architectural direction to C-level executives at major US client for data migration (GCP application and database components)',
        'Transformed delivery pipeline to zero-downtime for a public sector client'
      ],
      technologies: ['AWS', 'GCP', 'Kubernetes autoscaling', 'CI/CD pipelines']
    },
    {
      role: 'Lead Solution Architect',
      company: 'Signicat',
      period: 'Jun 2017 - Sep 2018',
      description: 'Led digital identity solutions for fintechs and banks, managing C-level stakeholders and driving technical partnerships in behavioral biometrics and AI-based analysis.',
      highlights: [
        'Created mobile demos for clients and technical content for RFPs',
        'Advised on the best vendors to partner with on behavioral biometrics and AI based analysis based on technical prototypes',
        'Managed C-level executives and senior management at fintechs and banks to understand their digital identity requirements',
        'Led developers for client engagement & digital transformation'
      ],
      technologies: ['Mobile app development (Ionic)', 'ReactJS', 'MongoDB', 'Redis', 'iOS', 'Android SDK', 'Partner Investor relations']
    },
    {
      role: 'Financial Data Analysis/Part time student',
      company: 'LSE',
      period: 'Sep 2016 - Jun 2018',
      description: 'Implemented algorithms and data analysis for portfolio management and asset classes as part of financial engineering course.',
      technologies: ['Python', 'R', 'Anaconda', 'Jupyter']
    },
    {
      role: 'Senior Consultant',
      company: 'MarkLogic',
      period: 'Jun 2015 - Apr 2017',
      description: 'Delivered big data solutions and semantic web applications for major UK banks and public sector, creating innovative financial instrument ontologies for trading strategies.',
      highlights: [
        'Big data and statistical analysis of unstructured data (including cleansing, analysis and reports) at major banks and public sector in the UK',
        'Created an open source semantic web solution that couples existing financial instruments ontologies with real data available from multiple sources, for analysis and trading strategies'
      ],
      technologies: ['Java', 'Angular', 'Spark', 'Storm', 'Mesos (two-level scheduler)', 'Docker', 'Semantics (RDF)', 'MarkLogic']
    },
    {
      role: 'CTO/Developer',
      company: 'Whimsy',
      period: 'Mar 2015 - Mar 2016',
      description: 'Founded and led technical development of travel app startup, creating cross-platform mobile solutions with intelligent search algorithms and predictive routing technology.',
      highlights: [
        'Created the mobile app in Ionic for cross-platform quicker delivery',
        'Developed search algorithm to identify the cheapest destinations for users',
        'Researched a self-learning predictive algorithm to find cheaper alternative route',
        'Made decisions on technology, design, cloud servers, data and coding'
      ],
      technologies: ['WebSockets', 'NodeJs', 'Angular', 'multithreading', 'Mongo', 'Redis']
    },
    {
      role: 'Lead Server Architect',
      company: 'Contract at multiple Game Studios',
      period: 'Jan 2012 - Jun 2015',
      description: 'Owned and led complete backend development including application architecture, API for multiple microservices and messaging frameworks.',
      highlights: [
        'Gunjin Games: Laid the foundation for the back-end design of Gunjin\'s multiplayer game',
        'Hutch Games: Designed the complete back-end for Hutch\'s F2P multiplayer racing game',
        'Supermassive Games: Implemented the complete back-end in Netty(java) including client-server communication libraries for F2P game',
        'Hooplo Media: Hands-on Team Lead and dev-op, responsible for design and development of the back-end for studio\'s in-house games'
      ],
      technologies: ['Devops', 'C#', 'Unity', 'Gamesparks', 'Websockets', 'MongoDB', 'CouchDB', 'Mysql', 'Cassandra', 'Spring Boot and MVC', 'Adobe Livecycle Data Services', 'Hibernate']
    },
    {
      role: 'Senior Java Developer',
      company: 'Electronic Arts',
      period: 'Aug 2009 - Nov 2011',
      description: 'Architected and developed high-scale backend systems for EA games serving 2-3 million daily users, implementing modern technologies like Scala and Cassandra for mission-critical features.',
      highlights: [
        'Responsible for the complete back-end development of games that catered to a user base of 2-3 million per day',
        'Re-wrote the architecture of rewards feature for in-game missions with Scala and Cassandra'
      ],
      technologies: ['Spring', 'Protobuf', 'Avro', 'Scala', 'Cassandra', 'Struts', 'Hibernate', 'Postgresql']
    },
    {
      role: 'Web Developer',
      company: 'IG Index',
      period: 'Jan 2008 - May 2009',
      description: 'Implemented several mobile features including push notifications, price alerts, DMA prices that used Lightstreamer server to provide real-time streaming prices using YUI libraries.',
      technologies: ['Lightstreamer', 'Jabber XMPP', 'Hessian web service protocol', 'Hibernate', 'JMS', 'JDBC', 'YUI']
    },
    {
      role: 'Full Stack Developer',
      company: 'India (Multiple Companies)',
      period: 'Apr 2003 - Sep 2007',
      description: 'Offshore IT consultant and developer for multiple companies including Morgan Stanley, Cisco, DHL, Infosys.',
      highlights: [
        'Morgan Stanley, Cisco, DHL, Infosys: Offshore IT consultant and developer for multiple companies',
        'Developed complete applications that included UX, database modelling and server coding'
      ],
      technologies: ['Spring', 'Structs', 'Hibernate', 'Java Beans', 'Ajax', 'Postgresql', 'Oracle']
    }
  ];

  return (
    <section id="experience" className="experience">
      <div className="container">
        <h2 className="section-title">Experience</h2>
        {/* <p className="section-subtitle">
          Over 21 years of building high-performance systems and leading technical teams
        </p> */}

        <div className="experience-timeline">
          {experience.map((exp, index) => (
            <div key={index} className="experience-item">
              <div className="experience-dot"></div>
              <div className="experience-content">
                <h3>{exp.role}</h3>
                <div className="experience-company">{exp.company}</div>
                <div className="experience-period">{exp.period}</div>
                <p>{exp.description}</p>
                {exp.highlights && (
                  <ul className="experience-highlights">
                    {exp.highlights.map((highlight, i) => (
                      <li key={i}>{highlight}</li>
                    ))}
                  </ul>
                )}
                {exp.technologies && (
                  <div className="experience-technologies">
                    <strong>Technologies:</strong>
                    <div className="tech-tags">
                      {exp.technologies.map((tech, i) => (
                        <span key={i} className="tech-tag">{tech}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
